import { ThirdPaneColumnHeader } from '../ThirdPane';
import HistoryTypePicker from './HistoryTypePicker';
import { Flex } from 'rebass';
import * as Accordion from '@radix-ui/react-accordion';
import TimelineItemAccordionHeader from './TimelineItemAccordionHeader';
import { groupHistoryItemsByYearDay } from './helpers';
import React, { useMemo } from 'react';
import { HistoryItem, HistoryItemsByYearDate } from './types';
import './Timeline.styles.css';
import TimelineYear from './TimelineYear';

type TimelineProps<T extends string> = {
  noRail?: boolean;
  historyItems: HistoryItem[];
  historyItemTypes?: T[];
  title?: string;
  selectedHistoryItemTypes?: T[];
  setSelectedHistoryItemTypes?: (selectedTypes: T[]) => void;
  scrollDivRef: any;
};

const Timeline = <T extends string>({
  noRail = false,
  title = 'History',
  selectedHistoryItemTypes,
  setSelectedHistoryItemTypes,
  historyItemTypes,
  historyItems,
  scrollDivRef,
}: TimelineProps<T>) => {
  // Group by year + date
  const groupedItems: HistoryItemsByYearDate = useMemo(
    () => historyItems.reduce(groupHistoryItemsByYearDay, {}),
    [historyItems]
  );

  // used to set all accordions open
  const years = Object.keys(groupedItems).sort((a, b) => b.localeCompare(a));

  const currentYear = new Date().getFullYear() + '';
  const onlyThisYear = years.length === 1 && years[0] === currentYear;

  return (
    <>
      <ThirdPaneColumnHeader title={title} />
      {historyItemTypes &&
        historyItemTypes.length > 1 &&
        setSelectedHistoryItemTypes && (
          <HistoryTypePicker
            selectedTypes={selectedHistoryItemTypes}
            availableTypes={historyItemTypes}
            onSelectionChange={setSelectedHistoryItemTypes}
            px={4}
            pb="2"
          />
        )}
      <Flex
        flexDirection="column"
        flexGrow={1}
        flexShrink={1}
        height="100%"
        overflowY="auto"
        ref={scrollDivRef}
        pl={4}
        // pr={3}
        pt={0}
        sx={{ boxSizing: 'border-box' }}
      >
        {onlyThisYear ? (
          <TimelineYear
            noRail={noRail}
            itemsByDay={groupedItems[currentYear]}
          />
        ) : (
          <Accordion.Root
            key={JSON.stringify(years)}
            type="multiple"
            defaultValue={years}
          >
            {years.map((year, yearIndex) => {
              const isFirstYear = yearIndex === 0;
              const isLastYear =
                yearIndex + 1 === Object.keys(groupedItems).length;

              return (
                <Accordion.Item
                  style={{ paddingBottom: '8px', position: 'relative' }}
                  value={year}
                  className="radix--AccordionItem"
                  key={year}
                >
                  <TimelineItemAccordionHeader
                    year={year}
                    isFirst={isFirstYear}
                    isLast={isLastYear}
                  />
                  <Accordion.Content className="radix--AccordionContent">
                    <TimelineYear
                      noRail={noRail}
                      itemsByDay={groupedItems[year]}
                    />
                  </Accordion.Content>
                </Accordion.Item>
              );
            })}
          </Accordion.Root>
        )}
      </Flex>
    </>
  );
};

export default Timeline;
